import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

export const BIRTHDATE_KEY: string = "bee-birthdate";

// Low and High bounds with Average, in days.
export interface IAgeRange {
  low: number;
  high: number;
}

export class BeeType {
  name: string;
  type: BEE_TYPE;
  range: IAgeRange;
  average?: number; // overrides the low/high midpoint when set
  hidden: boolean = false;
}

export const HONEY_BEE_AGE: IAgeRange = {
  low: 122,
  high: 150
}

export const WASP_AGE: IAgeRange = {
  low: 12,
  high: 22
}

export const HORNET_AGE: IAgeRange = {
  low: 154,
  high: 213
}

export const SPIDER_AGE: IAgeRange = {
  low: 280,
  high: 540
}

export enum BEE_TYPE {
  HONEY,
  WASP,
  HORNET,
  SPIDER
}

export const BEE_TYPES: BeeType[] = [
  <BeeType>{
    name: "Western Honey Bee",
    type: BEE_TYPE.HONEY,
    range: HONEY_BEE_AGE,
    average: 130
  }, <BeeType> {
    name: "Yellowjacket Wasp",
    type: BEE_TYPE.WASP,
    range: WASP_AGE
  }, <BeeType> {
    name: "MURDER HORNET",
    type: BEE_TYPE.HORNET,
    range: HORNET_AGE
  }, <BeeType> {
    name: "Spider",
    type: BEE_TYPE.SPIDER,
    range: SPIDER_AGE,
    hidden: true
  }
]

@Injectable({
  providedIn: 'root'
})
export class AgeService {
  private _birthdate: Date;
  private _beeLifespan: number;
  private _beeRange: IAgeRange;

  constructor(public cookies: CookieService) { 
    var loadedDate = localStorage.getItem(BIRTHDATE_KEY);
    if (!!loadedDate)
      this._birthdate = new Date(parseInt(loadedDate, 10));
    else
      this._birthdate = null;

      this.setBeeType(BEE_TYPES[0]);
  }

  get currentDate(): Date {
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  get birthdate(): Date {
    return this._birthdate;
  }

  set birthdate(date: Date) {
    this._birthdate = date;
    localStorage.setItem(BIRTHDATE_KEY, ''+this._birthdate.getTime());
    this._nextGoldenBeeDay = null;
  }

  get dayAge(): number {
    return (this.currentDate.getTime() - this.birthdate.getTime()) / (1000*60*60*24);
  }

  get beeRange(): IAgeRange {
    return this._beeRange;
  }

  get beeLifespan(): number {
    return this._beeLifespan;
  }

  set beeLifespan(val: number) {
    this._beeLifespan = Math.max(Math.min(val, this._beeRange.high), this._beeRange.low);
    this._nextGoldenBeeDay = null;
  }

  dateToDays(date: Date): number {
    var localMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    var epoch = new Date(1970, 0, 1);
    return Math.round((localMidnight.getTime() - epoch.getTime()) / (1000*60*60*24));
  }

  daysToDate(days: number): Date {
    return new Date(1970, 0, 1 + days);
  }

  daysToBeeAge(days: number, beeDays?: number): number {
    beeDays = beeDays || this._beeLifespan;
    var beelifes = days / beeDays;
    return Math.round(beelifes * 100) / 100;
  }

  isToday(date: Date) {
    return this.dateToDays(date) == this.dateToDays(this.currentDate);
  }

  isTomorrow(date: Date) {
    return this.dateToDays(date) == this.dateToDays(this.currentDate) + 1;
  }

  wasYesterday(date: Date) {
    return this.dateToDays(date) == this.dateToDays(this.currentDate) - 1 ||
           this.dateToDays(date) - this._beeLifespan == this.dateToDays(this.currentDate) - 1;
  }

  _getBeeAge(beeDays?: number) {
    beeDays = beeDays || this._beeLifespan;
    if (!this.birthdate)
      return 0;
    return this.daysToBeeAge(this.dayAge, beeDays);
  }

  get beeAge(): number {
    return this._getBeeAge(this._beeLifespan);
  }

  beeAgeRange(type: BEE_TYPE): {min: number, max: number} {
    var range = BEE_TYPES.find(t => t.type == type).range;

    return {
      min: this._getBeeAge(range.high),
      max: this._getBeeAge(range.low)
    };
  }

  get nextBeeDay(): Date {
    var totalDays = Math.floor(this.dayAge);
    var mod = totalDays % this._beeLifespan;
    if (mod == 0)
      return this.currentDate;
    var nextDay = this.dateToDays(this.currentDate) + (this._beeLifespan - mod);
    var nextDate = this.daysToDate(nextDay);
    return nextDate;
  }

  setBeeType(type: BeeType) {
    this._beeRange = type.range;
    this.beeLifespan = type.average || Math.round((type.range.low + type.range.high) / 2);
  }

  _getBeeDays(startDate: Date, count: number): Date[] {
    var date = startDate;
    var beeDays = [date];
    
    for(var i = 1; i < count; i++) {
      date = this.daysToDate(this.dateToDays(date) + this._beeLifespan);
      beeDays.push(date);
    }

    return beeDays;
  }

  _getHumanBirthdays(count: number): Date[] {
    var startYear = this.birthdate.getFullYear();
    var birthMonth = this.birthdate.getMonth();
    var birthDay = this.birthdate.getDate();
    var bday = new Date(startYear + 1, birthMonth, birthDay);
    var bdays = [bday];

    for (var i = 1; i < count; i ++) {
      bday = new Date(bday.getFullYear() + 1, birthMonth, birthDay);
      bdays.push(bday);
    }

    return bdays;
  }

  _getGoldenBeeDays(maxHumanYears?: number): Date[] {
    maxHumanYears = maxHumanYears || 200;
    var birthMonth = this.birthdate.getMonth();
    var birthday = this.birthdate.getDate();
    var beeDays = this._getBeeDays(this.birthdate, (maxHumanYears * 365) / this._beeLifespan);
    var goldenBeeDays: Date[] = beeDays.filter(bd => bd.getMonth() == birthMonth && bd.getDate() == birthday);
    return goldenBeeDays;
  }

  _getFutureGoldenBeeDays(maxHumanYears?: number): Date[] {
    maxHumanYears = maxHumanYears || 200;
    var currentDay = this.dateToDays(this.currentDate);
    var goldenBeeDays = this._getGoldenBeeDays(maxHumanYears).filter(bd => this.dateToDays(bd) >= currentDay);
    return goldenBeeDays;
  }

  _nextGoldenBeeDay: Date;
  get nextGoldenBeeDay(): Date {
    if (!!this._nextGoldenBeeDay)
      return this._nextGoldenBeeDay;

    var futureGBDs = this._getFutureGoldenBeeDays();
    if (futureGBDs.length <= 0)
      futureGBDs = this._getFutureGoldenBeeDays(10000);
    if (futureGBDs.length <= 0)
      return null;
    else
      this._nextGoldenBeeDay = futureGBDs[0];

    return this._nextGoldenBeeDay;
  }
}
